import React from 'react';

export interface ProductRangeDraft {
  selectedCustomers: string[]
  matrix: Record<string, string[]>
  productAreaFilter: string
  familyFilter: string
  expandedAreas: Record<string, boolean>
}

const PRODUCT_RANGE_DRAFT_KEY = 'productRangeMatrixDraft';

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const isStringRecord = (value: unknown): value is Record<string, string[]> =>
  typeof value === 'object' &&
  value !== null &&
  Object.values(value).every((item) => isStringArray(item));

const isBooleanRecord = (value: unknown): value is Record<string, boolean> =>
  typeof value === 'object' &&
  value !== null &&
  Object.values(value).every((item) => typeof item === 'boolean');

const isProductRangeDraft = (value: unknown): value is ProductRangeDraft => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const draft = value as Partial<ProductRangeDraft>;

  return (
    isStringArray(draft.selectedCustomers) &&
    isStringRecord(draft.matrix) &&
    typeof draft.productAreaFilter === 'string' &&
    typeof draft.familyFilter === 'string' &&
    isBooleanRecord(draft.expandedAreas)
  );
};

const readProductRangeDraft = (): ProductRangeDraft | null => {
  const storedDraft = sessionStorage.getItem(PRODUCT_RANGE_DRAFT_KEY);
  if (storedDraft === null) {
    return null;
  }

  try {
    const parsedDraft: unknown = JSON.parse(storedDraft);
    if (!isProductRangeDraft(parsedDraft)) {
      sessionStorage.removeItem(PRODUCT_RANGE_DRAFT_KEY);
      return null;
    }

    return parsedDraft;
  } catch {
    sessionStorage.removeItem(PRODUCT_RANGE_DRAFT_KEY);
    return null;
  }
};

export const useProductRangeDraft = () => {
  const [draft, setDraft] = React.useState<ProductRangeDraft | null>(() => readProductRangeDraft());

  const saveDraft = React.useCallback((nextDraft: ProductRangeDraft) => {
    sessionStorage.setItem(PRODUCT_RANGE_DRAFT_KEY, JSON.stringify(nextDraft));
    setDraft(nextDraft);
  }, []);

  const clearDraft = React.useCallback(() => {
    sessionStorage.removeItem(PRODUCT_RANGE_DRAFT_KEY);
    setDraft(null);
  }, []);

  return { draft, saveDraft, clearDraft };
};
